import { Fragment, type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '../box/index';
import { Icon } from '../icon/index';
import { Stack } from '../stack/index';
import { WizardStep, WizardValidation } from './hooks';

type WizardStepVisual = 'completed' | 'active' | 'error' | 'incomplete';

// Makes a clickable step Box behave as a keyboard-operable button: focusable and activated by Enter/Space.
const stepInteractionProps = (disabled: boolean, onClick: () => void) => ({
	role: 'button' as const,
	tabIndex: disabled ? -1 : 0,
	'aria-disabled': disabled || undefined,
	onClick: disabled ? undefined : onClick,
	onKeyDown: disabled
		? undefined
		: (e: KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onClick();
				}
			}
});

const WizardStepDot = ({ visual, number }: { visual: WizardStepVisual; number: number }) => (
	<Box
		aria-hidden
		sx={{
			flexShrink: 0,
			width: '26px',
			height: '26px',
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			color: theme => theme.palette.primary.contrastText,
			bgcolor: theme =>
				visual === 'completed'
					? theme.palette.success.main
					: visual === 'active'
						? theme.palette.primary.main
						: visual === 'error'
							? theme.palette.error.main
							: theme.palette.text.disabled
		}}
	>
		{visual === 'completed' ? (
			<Icon iconName="Check" color="inherit" sx={{ fontSize: 16 }} />
		) : visual === 'error' ? (
			<Icon iconName="PriorityHigh" color="inherit" sx={{ fontSize: 16 }} />
		) : (
			<Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: '20px', letterSpacing: '0.14px' }}>
				{number}
			</Box>
		)}
	</Box>
);

const WizardStepPill = ({
	title,
	visual,
	number,
	disabled,
	onClick
}: {
	title: string;
	visual: WizardStepVisual;
	number: number;
	disabled: boolean;
	onClick: () => void;
}) => (
	<Box
		{...stepInteractionProps(disabled, onClick)}
		aria-current={visual === 'active' ? 'step' : undefined}
		sx={{
			display: 'inline-flex',
			alignItems: 'center',
			flexShrink: 0,
			p: '0.5rem',
			borderRadius: '999px',
			border: '1px solid',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			cursor: disabled ? 'default' : 'pointer',
			opacity: disabled ? 0.5 : 1,
			borderColor: theme =>
				visual === 'completed'
					? theme.palette.success.main
					: visual === 'active'
						? theme.palette.primary.main
						: visual === 'error'
							? theme.palette.error.main
							: theme.palette.swiftfox.neutral['40-justGrey'],
			bgcolor: theme => (visual === 'active' ? theme.palette.primary.light : 'transparent')
		}}
	>
		<WizardStepDot visual={visual} number={number} />
		<Box
			component="span"
			sx={{
				px: 1,
				fontSize: '0.875rem',
				fontWeight: 500,
				lineHeight: 1.2,
				letterSpacing: '0.4px',
				color: theme => theme.palette.text.primary
			}}
		>
			{title}
		</Box>
	</Box>
);

type WizardStepData = { title: string; visual: WizardStepVisual; number: number; disabled: boolean; onClick: () => void };

/**
 * Compact fallback used when the pills don't fit: number circle on top, label below.
 * Each step is an equal-width column so the circles are evenly spaced; the connector is absolutely
 * positioned to run from the previous circle to this one (at circle-centre height), and the label is
 * centred in the column beneath the circle.
 */
const WizardStepCompact = ({ title, visual, number, disabled, onClick, showConnector }: WizardStepData & { showConnector: boolean }) => (
	<Box
		{...stepInteractionProps(disabled, onClick)}
		aria-current={visual === 'active' ? 'step' : undefined}
		sx={{
			flex: 1,
			minWidth: 0,
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: 1,
			cursor: disabled ? 'default' : 'pointer',
			opacity: disabled ? 0.5 : 1
		}}
	>
		{showConnector ? (
			<Box
				aria-hidden
				sx={{ position: 'absolute', top: '13px', left: 'calc(-50% + 17px)', right: 'calc(50% + 17px)', height: '1px', bgcolor: 'divider' }}
			/>
		) : null}
		<WizardStepDot visual={visual} number={number} />
		<Box
			component="span"
			sx={{
				maxWidth: '100%',
				textAlign: 'center',
				fontSize: '0.75rem',
				fontWeight: 500,
				lineHeight: 1.2,
				letterSpacing: '0.4px',
				color: theme => (visual === 'active' ? theme.palette.primary.main : theme.palette.text.primary)
			}}
		>
			{title}
		</Box>
	</Box>
);

export const WizardStepper = <T extends object>({
	steps,
	step,
	stepValidations,
	onStepChange
}: {
	steps: WizardStep<T>[];
	step: number;
	stepValidations: WizardValidation[];
	onStepChange: (index: number) => void;
}) => {
	const slotRef = useRef<HTMLDivElement>(null);
	const rightSentinelRef = useRef<HTMLDivElement>(null);
	const [compact, setCompact] = useState(false);

	// Single pass with running counters: displayed numbers count 1,2,3… across visible steps
	// (ignoring suppressed ones), and a disabled step disables every step after it.
	const items: WizardStepData[] = [];
	let suppressedSoFar = 0;
	let disabledSeen = false;
	for (let i = 0; i < steps.length; i++) {
		const validation = stepValidations[i];
		if (validation === WizardValidation.Suppressed) {
			suppressedSoFar++;
			continue;
		}
		const currentStepValid = validation === WizardValidation.Valid;
		const visual: WizardStepVisual =
			step === i ? 'active' : i < step && currentStepValid ? 'completed' : i < step && !currentStepValid ? 'error' : 'incomplete';
		items.push({
			title: steps[i].title,
			visual,
			number: i - suppressedSoFar + 1,
			disabled: validation === WizardValidation.Disabled || disabledSeen,
			onClick: () => onStepChange(i)
		});
		if (validation === WizardValidation.Disabled) disabledSeen = true;
	}

	// Collapse the pills to the compact (number-over-label) layout when they can't fit the
	// screen-centred slot. We measure a hidden, always-full-size pill copy — not the visible
	// stepper — so the fit test stays stable and doesn't oscillate once collapsed. The copy is
	// left-aligned, so only its right edge can overflow: a 1px sentinel there leaves the slot (minus a
	// small threshold) when the pills no longer fit, and re-enters once there's room for them again.
	useLayoutEffect(() => {
		const slot = slotRef.current;
		const right = rightSentinelRef.current;
		if (!slot || !right) return;

		const THRESHOLD = 8;
		const fits = () => right.getBoundingClientRect().right <= slot.getBoundingClientRect().right - THRESHOLD + 0.5;
		setCompact(!fits());

		const observer = new IntersectionObserver(entries => entries.forEach(entry => setCompact(!entry.isIntersecting)), {
			root: slot,
			threshold: 1,
			rootMargin: `0px -${THRESHOLD}px 0px 0px`
		});
		observer.observe(right);
		return () => observer.disconnect();
	}, [items.length]);

	// The parent provides the available gap between the wizard's action clusters. This overlay keeps
	// the stepper centred within that gap while allowing the visible controls to opt back into clicks.
	return (
		<Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
			<Box
				ref={slotRef}
				sx={{
					width: '100%',
					minWidth: 0,
					position: 'relative',
					overflow: 'hidden',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}
			>
				{/* Hidden full-size pill copy used only to measure whether the pills fit — kept stable regardless of the visible mode. */}
				<Box aria-hidden sx={{ position: 'absolute', top: 0, left: 0, visibility: 'hidden', display: 'flex', alignItems: 'center' }}>
					{items.map((it, idx) => (
						<Fragment key={`measure-${it.title}`}>
							{idx > 0 ? <Box sx={{ width: '24px', height: '1px', flexShrink: 0 }} /> : null}
							<WizardStepPill {...it} />
						</Fragment>
					))}
					<Box ref={rightSentinelRef} sx={{ width: '1px', height: '1px', flexShrink: 0 }} />
				</Box>

				{compact ? (
					<Stack
						direction="row"
						alignItems="flex-start"
						sx={{ width: '100%', maxWidth: `${items.length * 96}px`, minWidth: 0, pointerEvents: 'auto' }}
					>
						{items.map((it, idx) => (
							<WizardStepCompact key={it.title} {...it} showConnector={idx > 0} />
						))}
					</Stack>
				) : (
					<Stack direction="row" alignItems="center" justifyContent="center" sx={{ minWidth: 0, pointerEvents: 'auto' }}>
						{items.map((it, idx) => (
							<Fragment key={it.title}>
								{idx > 0 ? <Box sx={{ width: '24px', height: '1px', flexShrink: 0, bgcolor: 'divider' }} /> : null}
								<WizardStepPill {...it} />
							</Fragment>
						))}
					</Stack>
				)}
			</Box>
		</Box>
	);
};
